import { useEffect, useRef } from 'react';
import { useMeditationPlayerStore } from '@/store/meditationPlayerStore';
import { updateMeditationProgress } from '@/hooks/useMeditations';

/**
 * Невидимый глобальный <audio> элемент.
 * 
 * Рендерится один раз в App.tsx и НИКОГДА не размонтируется.
 * Управляется через useMeditationPlayerStore.
 * 
 * Это решает проблему: музыка обрывается при переходе с экрана плеера.
 */
export function GlobalAudioPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastMeditationIdRef = useRef<string | null>(null);
  
  const {
    currentMeditation,
    loop,
    historyId,
    isPlaying,
    setAudioElement,
    setCurrentTime,
    setDuration,
    setIsPlaying,
  } = useMeditationPlayerStore();
  
  // Регистрируем audio в store при монтировании
  useEffect(() => {
    if (audioRef.current) {
      setAudioElement(audioRef.current);
    }
    return () => {
      setAudioElement(null);
    };
  }, []);
  
  // 🆕 При смене трека — перезагружаем audio и запускаем воспроизведение
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentMeditation) return;
    
    // Если поменялся трек — load + play
    if (lastMeditationIdRef.current !== currentMeditation.id) {
      lastMeditationIdRef.current = currentMeditation.id;
      
      audio.load();
      // Авто-воспроизведение нового трека
      audio.play().catch((e) => {
        console.warn('[global audio] autoplay blocked:', e);
        // Если autoplay заблокирован — юзер должен тапнуть play вручную
      });
    }
  }, [currentMeditation?.id]);
  
  // Сохранение прогресса каждые 5 секунд во время воспроизведения
  useEffect(() => {
    if (!historyId || !isPlaying) return;
    
    const interval = setInterval(() => {
      const audio = audioRef.current;
      if (audio) {
        updateMeditationProgress(historyId, audio.currentTime, false);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [historyId, isPlaying]);
  
  function handleEnded() {
    const audio = audioRef.current;
    if (!audio || !currentMeditation) return;
    
    if (loop) return;
    
    if (historyId) {
      updateMeditationProgress(historyId, currentMeditation.duration_seconds, true);
    }
    
    setIsPlaying(false);
  }
  
  if (!currentMeditation) return null;
  
  return (
    <audio
      ref={audioRef}
      id="global-audio-player"
      src={currentMeditation.audio_url}
      loop={loop}
      preload="auto"
      onLoadedMetadata={(e) => {
        const a = e.target as HTMLAudioElement;
        setDuration(a.duration || currentMeditation.duration_seconds);
      }}
      onTimeUpdate={(e) => {
        const a = e.target as HTMLAudioElement;
        setCurrentTime(a.currentTime);
      }}
      onEnded={handleEnded}
      onPause={() => setIsPlaying(false)}
      onPlay={() => setIsPlaying(true)}
      style={{ display: 'none' }}
    />
  );
}
